import { PlayStatus } from '@ael/shared-components-library';
import Immutable from 'immutable';
import {Guid} from '../../../model/guid';

enum AudioAction {
  Initialized = 'AudioAction_Initialized',
  TimeUpdated = 'AudioAction_TimeUpdated',
  PlayStatusChanged = 'AudioAction_PlayStatusChanged',
}

export type ContentItemAudioState = {
  playStatus: PlayStatus;
  currentTime: number;
  duration: number | null;
  hasEndedOnce: boolean;
};

export const AudioInitialized = (guid: Guid, duration: number) => {
  return {
    type: AudioAction.Initialized,
    payload: {
      guid: guid,
      duration: duration,
    },
  };
};

export const AudioCurrentTimeUpdated = (guid: Guid, currentTime: number) => {
  return {
    type: AudioAction.TimeUpdated,
    payload: {
      guid: guid,
      currentTime: currentTime,
    },
  };
};

export const AudioPlayStatusChanged = (guid: Guid, playStatus: PlayStatus) => {
  return {
    type: AudioAction.PlayStatusChanged,
    payload: {
      guid: guid,
      playStatus: playStatus,
    },
  };
};

export type AudioReducerState = Immutable.Map<Guid, ContentItemAudioState>;

export function audioStateReducer(
  state: AudioReducerState = Immutable.Map<Guid, ContentItemAudioState>(),
  action: any
): AudioReducerState {
  switch (action.type) {
    case AudioAction.Initialized: {
      const typedAction = action as ReturnType<typeof AudioInitialized>;
      const contentItemAudioState = state.get(typedAction.payload.guid);
      const hasEndedBefore = contentItemAudioState?.hasEndedOnce ?? false;
      return state.set(action.payload.guid, {
        duration: action.payload.duration,
        currentTime: 0,
        playStatus: PlayStatus.NotStartedOrPaused,
        hasEndedOnce: hasEndedBefore,
      });
    }

    case AudioAction.TimeUpdated: {
      const typedAction = action as ReturnType<typeof AudioCurrentTimeUpdated>;
      const contentItemAudioState = state.get(typedAction.payload.guid);
      if (!contentItemAudioState) {
        return state;
      }

      return state.set(action.payload.guid, {
        ...contentItemAudioState,
        currentTime: typedAction.payload.currentTime,
      });
    }

    case AudioAction.PlayStatusChanged: {
      const typedAction = action as ReturnType<typeof AudioPlayStatusChanged>;
      const contentItemAudioState = state.get(typedAction.payload.guid);
      if (!contentItemAudioState) {
        return state;
      }

      const didFinishInThisAction = typedAction.payload.playStatus === PlayStatus.Finished;
      const currentTime = didFinishInThisAction ? 0 : contentItemAudioState.currentTime;

      return state.set(action.payload.guid, {
        ...contentItemAudioState,
        playStatus: PlayStatus.Finished,
        hasEndedOnce: contentItemAudioState.hasEndedOnce || didFinishInThisAction,
        currentTime,
      });
    }

    default:
      return state;
  }
}
